function transpose<T, U>(matrix: T[][], defaultValue: U) {
  const maxColumn = Math.max(...matrix.map(l => l.length));

  return Array.from({ length: maxColumn }).map((_, columnIndex) =>
    matrix.map(line => line[columnIndex] ?? defaultValue),
  );
}

function getDeepMapCopy(map: Map<string, string[]>) {
  return new Map<string, string[]>(JSON.parse(JSON.stringify(Array.from(map))));
}

function getCSVLines(content: string) {
  const normalizedContent = content.replace(/\r/g, "");
  const lines: string[][] = [];
  let currentLine: string[] = [];
  let currentCell = "";
  let isInsideQuotes = false;

  for (let i = 0; i < normalizedContent.length; i += 1) {
    const currentCharacter = normalizedContent[i];

    if (currentCharacter === '"') {
      if (isInsideQuotes && normalizedContent[i + 1] === '"') {
        currentCell += '"';
        i += 1;
        continue;
      }

      isInsideQuotes = !isInsideQuotes;
      continue;
    }

    if (currentCharacter === ";" && !isInsideQuotes) {
      currentLine.push(currentCell);
      currentCell = "";
      continue;
    }

    if (currentCharacter === "\n" && !isInsideQuotes) {
      currentLine.push(currentCell);

      if (currentLine.some(cell => cell.length > 0)) {
        lines.push(currentLine);
      }

      currentLine = [];
      currentCell = "";
      continue;
    }

    currentCell += currentCharacter;
  }

  if (currentCell.length > 0 || currentLine.length > 0) {
    currentLine.push(currentCell);

    if (currentLine.some(cell => cell.length > 0)) {
      lines.push(currentLine);
    }
  }

  return lines;
}

// { name: [name], inputModelName: [inputModelName, binding...], outputModelName: [outputModelName]}
export interface RowValue {
  [key: string]: string | null;
}
function parseCSV(content: string, axis: "line" | "col"): RowValue[];
function parseCSV(
  content: string,
  axis: "line" | "col",
  headerMapping: Map<string, string[]>,
): RowValue[] | undefined;
function parseCSV(
  content: string,
  axis: "line" | "col",
  headerMapping: Map<string, string[]>,
  isFailureFile: boolean,
): RowValue[] | undefined;
function parseCSV(
  content: string,
  axis: "line" | "col" = "line",
  headerMapping?: Map<string, string[]>,
  isFailureFile?: boolean,
) {
  const headerMappingCopy = headerMapping ? getDeepMapCopy(headerMapping) : undefined;
  let lines = getCSVLines(content);

  if (axis === "col") {
    lines = transpose(lines, "");
  }

  const headers = lines.shift();

  if (!headers) return [];

  const mappingFileColumnToDBColumn: { fileHeaderIndex: number; dbColumn: string }[] = [];
  if (isFailureFile && headerMappingCopy) {
    for (const header of headers) {
      let found;
      for (const value of headerMappingCopy.values()) {
        found = value.find(v => v === header);
        if (found) break;
      }
      if (!found) {
        headerMappingCopy.set(header, [header]);
      }
    }
  }

  if (headerMappingCopy) {
    for (const [key, values] of headerMappingCopy) {
      const f = headers.findIndex(h => values.find(v => v === h));
      mappingFileColumnToDBColumn.push({ fileHeaderIndex: f, dbColumn: key });
    }
    const linesMap = lines.map(l =>
      mappingFileColumnToDBColumn.reduce<RowValue>((acc, mapping) => {
        const cell = l[mapping.fileHeaderIndex];
        acc[mapping.dbColumn] = cell === undefined || cell === "" ? null : cell;

        return acc;
      }, {}),
    );

    return linesMap;
  }

  const linesMap = lines.map(l =>
    headers.reduce<RowValue>((acc, header, i) => {
      const cell = l[i];
      acc[header] = cell === undefined || cell === "" ? null : cell;
      return acc;
    }, {}),
  );
  return linesMap;
}

export function getHeaders(content: string) {
  return getCSVLines(content).shift();
}

export function findUnkownColumns(content: string, expectedColumn: Map<string, string[]>) {
  const headers = getHeaders(content);

  if (!headers) return Array.from(expectedColumn.keys());

  return headers.filter(
    h =>
      !Array.from(expectedColumn.values())
        .flatMap(v => v)
        .find(v => h === v),
  );
}

export function getMissingFailuresMandatoryColumns(
  content: string,
  mandatoryColumns: Map<string, string[]>,
) {
  const headers = getHeaders(content);
  if (!headers) return Array.from(mandatoryColumns.keys());

  let missingMandatoryHeaders: string[] = [];

  for (const [key, values] of mandatoryColumns) {
    if (!values.find(mh => headers.find(h => mh === h))) {
      missingMandatoryHeaders = [...missingMandatoryHeaders, key];
    }
  }

  return missingMandatoryHeaders;
}

export default parseCSV;
